import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Container } from '../../ui/Container';
import { Button } from '../../ui/Button';
import { GeometricAccent } from '../../ui/GeometricAccent';
import { MosqueArchMask } from '../../ui/MosqueArchMask';
interface SubpageHeroProps {
  eyebrow: string;
  title: string;
  paragraphs: string[];
  primaryCta: {
    label: string;
    to: string;
  };
  secondaryCta?: {
    label: string;
    to: string;
  };
  image: string;
  imageAlt: string;
  /**
   * Compact hero: shorter landscape image and tighter top margin,
   * matching the main Children page hero. Defaults to the taller layout.
   */
  compact?: boolean;
  /**
   * When true, the hero image is rendered inside the approved mosque-dome
   * mask instead of the default rounded frame. Used across the Children
   * subpages to keep the mosque-arch visual language consistent.
   */
  masked?: boolean;
}
export function SubpageHero({
  eyebrow,
  title,
  paragraphs,
  primaryCta,
  secondaryCta,
  image,
  imageAlt,
  compact = false,
  masked = false
}: SubpageHeroProps) {
  const renderCta = (cta: {
    label: string;
    to: string;
  }, variant: 'primary' | 'secondary') => {
    const isExternal = /^https?:\/\//.test(cta.to);
    const destination = getEnglishPath(cta.to);
    const button = <Button variant={variant} size="lg" data-id="element-725" data-mp="3qzllzv1sahc8">
        {cta.label}
      </Button>;
    return isExternal ? <a href={cta.to} target="_blank" rel="noopener noreferrer" data-id="element-726" data-mp="3i0rn1y0zdevi">
        {button}
      </a> : <Link to={destination} data-id="element-727" data-mp="3d1exh5iq5tp8">{button}</Link>;
  };
  return <section className="relative overflow-hidden" data-id="element-728" data-mp="3d1x2ornd7i9a">
      <GeometricAccent variant="arabesque" className="absolute -left-20 top-6 w-[420px] h-[420px] hidden md:block" opacity={0.08} data-id="element-729" data-mp="3833rx6zgq5ct" />
      <GeometricAccent variant="dots" className="absolute right-8 top-16 w-32 h-32" opacity={0.4} data-id="element-730" data-mp="1b7xu4e1mnf0a" />
      <Container className={`relative ${compact ? 'pt-8 md:pt-12 pb-16 md:pb-24' : 'py-16 md:py-24'}`} data-id="element-731" data-mp="30qw1ylnvp9t1">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center" data-id="element-732" data-mp="35j7b8f4ie7y9">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} className="lg:col-span-6 flex flex-col gap-6" data-id="element-733" data-mp="2qtrnl809yims">
            <span className="text-forest-700 font-semibold text-[15px]" data-id="element-734" data-mp="3oennmfvb6z6z">
              {eyebrow}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight text-ink-900" data-id="element-735" data-mp="3eikayg2tzi5s">
              {title}
            </h1>
            {paragraphs.map((p, i) => <p key={i} className="text-ink-700 text-base md:text-lg leading-relaxed max-w-xl" data-id="element-736" data-mp="233fa7hm9ftot" data-mpi={i}>
                {p}
              </p>)}
            <div className="flex flex-wrap gap-3 pt-2" data-id="element-737" data-mp="g77kfnfp1469">
              {renderCta(primaryCta, 'primary')}
              {secondaryCta && renderCta(secondaryCta, 'secondary')}
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.7,
          delay: 0.1,
          ease: [0.22, 1, 0.36, 1]
        }} className="lg:col-span-6 relative" data-id="element-738" data-mp="q4vhik0runrl">
            {masked ? <div className="relative w-full lg:w-[calc(100%+6rem)] lg:-ml-8 aspect-[200/148]" data-id="element-739" data-mp="6gco1q8qgo73">
                <MosqueArchMask src={image} alt={imageAlt} data-id="element-740" data-mp="3evmwdg37upnb" />
              </div> : <div className={`relative mx-auto ${compact ? 'aspect-[4/3] max-w-[560px]' : 'aspect-[4/5] max-w-[520px]'}`} data-id="element-741" data-mp="3aukuerhwzzhn">
                <div className={`absolute inset-0 ${compact ? 'rounded-t-[40%]' : 'rounded-t-[50%]'} rounded-b-3xl overflow-hidden bg-sage-100 shadow-[0_30px_80px_-30px_rgba(15,81,50,0.25)]`} data-id="element-742" data-mp="3sjoec8mhvwnc">
                  <img src={image} alt={imageAlt} className="w-full h-full object-cover" data-id="element-743" data-mp="64dgu0l40c7h" />
                </div>
                <div className={`absolute -inset-2 ${compact ? 'rounded-t-[40%]' : 'rounded-t-[50%]'} rounded-b-3xl border border-sand-300/40 pointer-events-none`} data-id="element-744" data-mp="f40gagua37xw" />
              </div>}
          </motion.div>
        </div>
      </Container>
    </section>;
}
function getEnglishPath(path: string) {
  if (!path.startsWith('/') || path.startsWith('/en')) return path;
  return `/en${path}`;
}